import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CircularProgress from "@mui/material/CircularProgress";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import NotesIcon from "@mui/icons-material/Notes";
import { PageHeader } from "@/components/PageHeader";
import { useAutoTour } from "@/components/walkthrough/useAutoTour";
import { TOOLS_TOUR } from "@/components/walkthrough/tours";

const CareerPathPanel = lazy(() =>
  import("./app.career-path").then((m) => ({ default: m.CareerPathPanel })),
);
const GoalsPanel = lazy(() =>
  import("./app.goals").then((m) => ({ default: m.GoalsPanel })),
);
const AutofillPanel = lazy(() =>
  import("./app.autofill").then((m) => ({ default: m.AutofillPanel })),
);
const LearningQueuePanel = lazy(() =>
  import("./app.learning-queue").then((m) => ({ default: m.LearningQueuePanel })),
);
const RejectionInsightsPanel = lazy(() =>
  import("./app.learning").then((m) => ({ default: m.RejectionInsightsPanel })),
);
const NotesPanel = lazy(() =>
  import("@/components/notes/NotesEditor").then((m) => ({ default: m.NotesPanel })),
);

function PanelFallback() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress size={28} />
    </Box>
  );
}

type ToolsSearch = { tab?: string };

const TAB_KEYS = ["career-path", "goals", "autofill", "learning-queue", "rejection-insights", "notes"] as const;
type TabKey = (typeof TAB_KEYS)[number];

export const Route = createFileRoute("/app/tools")({
  component: ToolsPage,
  head: () => ({ meta: [{ title: "Tools — JobYoda" }] }),
  validateSearch: (s: Record<string, unknown>): ToolsSearch => ({
    tab: typeof s.tab === "string" ? s.tab : undefined,
  }),
});

function ToolsPage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  useAutoTour(TOOLS_TOUR, "/app/tools");

  const activeTab: TabKey = (TAB_KEYS as readonly string[]).includes(tab ?? "")
    ? (tab as TabKey)
    : "career-path";

  const handleTabChange = (_: unknown, value: TabKey) => {
    navigate({ to: "/app/tools", search: { tab: value }, replace: true });
  };

  return (
    <Box>
      <PageHeader
        title="Tools"
        subtitle="Career planning, goals, autofill, learning, and rejection insights — all in one place."
      />

      <Card sx={{ mb: 3 }} data-tour="tools-tabs">
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab data-tour="tools-tab-career-path" value="career-path" icon={<AccountTreeIcon />} iconPosition="start" label="Career Path" />
          <Tab data-tour="tools-tab-goals" value="goals" icon={<FlagOutlinedIcon />} iconPosition="start" label="Goals" />
          <Tab data-tour="tools-tab-autofill" value="autofill" icon={<AutoFixHighIcon />} iconPosition="start" label="Autofill" />
          <Tab data-tour="tools-tab-learning-queue" value="learning-queue" icon={<SchoolOutlinedIcon />} iconPosition="start" label="Learning Queue" />
          <Tab data-tour="tools-tab-rejection-insights" value="rejection-insights" icon={<MarkEmailReadIcon />} iconPosition="start" label="Rejection Insights" />
          <Tab value="notes" icon={<NotesIcon />} iconPosition="start" label="Notes" />
        </Tabs>
      </Card>

      <Box>
        <Suspense fallback={<PanelFallback />}>
          {activeTab === "career-path" && <CareerPathPanel hideHeader />}
          {activeTab === "goals" && <GoalsPanel hideHeader />}
          {activeTab === "autofill" && <AutofillPanel hideHeader />}
          {activeTab === "learning-queue" && <LearningQueuePanel hideHeader />}
          {activeTab === "rejection-insights" && <RejectionInsightsPanel hideHeader />}
          {activeTab === "notes" && <NotesPanel hideHeader />}
        </Suspense>
      </Box>
    </Box>
  );
}
